import $ from "jquery";

/**
 * RTF Calendar Component
 * Displays round trip flights in a calendar format with prices
 */
const RTFCalendar = {
    STORAGE_KEY: "sb_booking_form_data",

    // Badge Euro icon SVG for lowest price indicator
    BADGE_EURO_ICON: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="M7 12h5"/><path d="M15 9.4a4 4 0 1 0 0 5.2"/></svg>`,

    init() {
        const $calendars = $("[data-rtf-calendar]");

        if ($calendars.length === 0) return;

        $calendars.each((_, element) => {
            this.initCalendar($(element));
        });
    },

    initCalendar($calendar) {
        const rtfData = JSON.parse($calendar.attr("data-rtf-data") || "{}");
        // Convert WordPress locale (nl_NL) to JavaScript locale (nl-NL)
        const locale = ($calendar.attr("data-locale") || "nl-NL").replace(
            "_",
            "-",
        );

        // Store state on the element
        $calendar.data("rtfData", rtfData);
        $calendar.data("locale", locale);
        $calendar.data("templateId", $calendar.attr("data-template-id"));
        $calendar.data(
            "leadFormPage",
            $calendar.attr("data-template-lead-form-page"),
        );

        // Determine the initial month to display
        // Start at current month if it has RTFs, otherwise jump to first available RTF month
        const today = new Date();
        const currentMonthKey = `${today.getFullYear()}-${String(
            today.getMonth() + 1,
        ).padStart(2, "0")}`;
        const monthsWithRtfs = rtfData.months_with_rtfs || [];

        if (
            monthsWithRtfs.length > 0 &&
            !monthsWithRtfs.includes(currentMonthKey)
        ) {
            // Current month has no RTFs, jump to first available month
            const [firstYear, firstMonth] = monthsWithRtfs[0]
                .split("-")
                .map(Number);
            $calendar.data(
                "currentDate",
                new Date(firstYear, firstMonth - 1, 1),
            );
        } else {
            $calendar.data("currentDate", new Date());
        }

        // Render month tabs
        this.renderMonthTabs($calendar);

        // Set localized weekday headers
        this.setWeekdayHeaders($calendar, locale);

        // Set initial view mode - URL parameter takes priority over data attribute
        const urlParams = new URLSearchParams(window.location.search);
        const urlView = urlParams.get("rtf-view");
        const dataView = $calendar.attr("data-initial-view") || "calendar";
        const initialView = urlView || dataView;
        $calendar.data("viewMode", initialView);

        // Render the current month (calendar view)
        this.renderMonth($calendar);

        // Render list view
        this.renderList($calendar);

        // Update active tab
        this.updateActiveTab($calendar);

        // Bind navigation and view toggle events
        this.bindEvents($calendar);

        // Apply initial view mode (show/hide correct view)
        this.setViewMode($calendar, initialView);
    },

    renderMonthTabs($calendar) {
        const rtfData = $calendar.data("rtfData");
        const locale = $calendar.data("locale");
        const monthsWithRtfs = rtfData.months_with_rtfs || [];
        const lowestPriceByMonth = rtfData.lowest_price_by_month || {};

        const $tabsContainer = $calendar.find("[data-month-tabs]");

        // Destroy existing slick if initialized
        if ($tabsContainer.hasClass("slick-initialized")) {
            $tabsContainer.slick("unslick");
        }

        $tabsContainer.empty();

        if (monthsWithRtfs.length === 0) return;

        monthsWithRtfs.forEach((monthKey) => {
            const [year, month] = monthKey.split("-").map(Number);
            const date = new Date(year, month - 1, 1);
            const monthName = date.toLocaleDateString(locale, {
                month: "long",
            });
            const lowestPrice = lowestPriceByMonth[monthKey]?.price;

            const formattedPrice = lowestPrice
                ? Math.round(lowestPrice).toLocaleString("nl-NL")
                : "-";

            const $tab = $(`
        <button type="button" class="sb_template-rtf-day-selector_month-tab" data-month-tab="${monthKey}">
        <span class="sb_template-rtf-day-selector_month-tab-name">${
            monthName.charAt(0).toUpperCase() + monthName.slice(1)
        } ${year}</span>
          <span class="sb_template-rtf-day-selector_month-tab-price">v.a. ${formattedPrice} p.p.</span>
        </button>
      `);

            $tab.on("click", () => {
                this.selectMonth($calendar, year, month - 1);
            });

            $tabsContainer.append($tab);
        });

        // Initialize slick if data-init-slick is present
        if ($tabsContainer.attr("data-init-slick") !== undefined) {
            const slickSettings = $tabsContainer.data("slick") || {};
            $tabsContainer.slick(slickSettings);
        }

        // Set initial active tab
        this.updateActiveTab($calendar);
    },

    updateActiveTab($calendar) {
        const currentDate = $calendar.data("currentDate");
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;

        const $tabsContainer = $calendar.find("[data-month-tabs]");

        $calendar.find("[data-month-tab]").removeClass("s_active");
        $calendar.find(`[data-month-tab="${monthKey}"]`).addClass("s_active");

        // Navigate slick to active tab if initialized
        if ($tabsContainer.hasClass("slick-initialized")) {
            const $activeTab = $calendar.find(`[data-month-tab="${monthKey}"]`);
            if ($activeTab.length) {
                const slideIndex = $activeTab
                    .closest(".slick-slide")
                    .data("slick-index");
                if (typeof slideIndex === "number") {
                    $tabsContainer.slick("slickGoTo", slideIndex, true);
                }
            }
        }
    },

    selectMonth($calendar, year, month) {
        const newDate = new Date(year, month, 1);
        $calendar.data("currentDate", newDate);
        this.renderMonth($calendar);
        this.renderList($calendar);
        this.updateActiveTab($calendar);
    },

    setWeekdayHeaders($calendar, locale) {
        // Get localized weekday names (short)
        const weekdays = [];
        const baseDate = new Date(2024, 0, 1); // A Monday

        for (let i = 0; i < 7; i++) {
            const date = new Date(baseDate);
            date.setDate(baseDate.getDate() + i);
            weekdays.push(
                date
                    .toLocaleDateString(locale, { weekday: "short" })
                    .substring(0, 2),
            );
        }

        // Set weekday headers (Monday first)
        $calendar.find("[data-weekday]").each((_, el) => {
            const dayIndex = parseInt($(el).attr("data-weekday"));
            // Convert Sunday (0) based index to Monday (1) based
            const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1;
            $(el).text(weekdays[adjustedIndex]);
        });
    },

    renderMonth($calendar) {
        const currentDate = $calendar.data("currentDate");
        const rtfData = $calendar.data("rtfData");
        const locale = $calendar.data("locale");
        const lowestPrices = rtfData.lowest_prices_by_date || {};
        const lowestPriceByMonth = rtfData.lowest_price_by_month || {};

        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // Update month header
        const monthName = currentDate.toLocaleDateString(locale, {
            month: "long",
            year: "numeric",
        });
        $calendar
            .find("[data-calendar-month]")
            .text(monthName.charAt(0).toUpperCase() + monthName.slice(1));

        // Get first and last day of month
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
        let startDayOfWeek = firstDay.getDay();
        // Convert to Monday-based (0 = Monday, 6 = Sunday)
        startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const $daysContainer = $calendar.find("[data-calendar-days]");
        $daysContainer.empty();

        // Get the month key for lowest price lookup (YYYY-MM format)
        const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
        const lowestPriceOfMonth = lowestPriceByMonth[monthKey]?.price;

        // Collect all dates in this month that have the lowest price
        const lowestPriceDays = [];
        if (lowestPriceOfMonth !== undefined) {
            Object.entries(lowestPrices).forEach(([dateKey, priceInfo]) => {
                // Use parseFloat to ensure numeric comparison
                if (
                    dateKey.startsWith(monthKey) &&
                    parseFloat(priceInfo.price) ===
                        parseFloat(lowestPriceOfMonth)
                ) {
                    lowestPriceDays.push(dateKey);
                }
            });
        }

        // Add empty cells for days before the first day of month
        for (let i = 0; i < startDayOfWeek; i++) {
            $daysContainer.append(
                '<div class="sb_template-rtf-day-selector_calendar-day s_empty"></div>',
            );
        }

        // Get selected RTF ID from URL
        const urlParams = new URLSearchParams(window.location.search);
        const selectedRtfId = urlParams.get("rtf-id");

        // Add days of the month
        for (let day = 1; day <= lastDay.getDate(); day++) {
            const date = new Date(year, month, day);
            const dateKey = this.formatDateKey(date);
            const isPast = date < today;
            const isToday = date.getTime() === today.getTime();
            const rtfInfo = lowestPrices[dateKey];
            const isLowestPriceOfMonth = lowestPriceDays.includes(dateKey);
            const isSelected =
                rtfInfo &&
                selectedRtfId &&
                String(rtfInfo.rtf_id) === selectedRtfId;

            let dayClasses = "sb_template-rtf-day-selector_calendar-day";
            let dayContent = `<span class="sb_template-rtf-day-selector_calendar-day-number">${day}</span>`;

            if (isToday) {
                dayClasses += " s_today";
            }

            if (isSelected) {
                dayClasses += " s_selected";
            }

            // Check availability for this day
            // availability = -1 means unlimited availability
            let isBookable = false;
            if (rtfInfo) {
                const hasUnlimitedAvailability = rtfInfo.availability === -1;
                if (hasUnlimitedAvailability) {
                    isBookable = rtfInfo.available !== false;
                } else {
                    const totalSpots = rtfInfo.availability || 0;
                    const bookedSpots = rtfInfo.total_persons || 0;
                    const availableSpots = totalSpots - bookedSpots;
                    isBookable =
                        rtfInfo.available !== false && availableSpots > 0;
                }
            }

            if (isPast) {
                dayClasses += " s_disabled";
            } else if (rtfInfo) {
                dayClasses += " s_has-rtf";
                if (!isBookable) {
                    dayClasses += " s_unavailable";
                }
                if (isLowestPriceOfMonth && isBookable) {
                    dayClasses += " s_lowest";
                    dayContent += `<span class="sb_template-rtf-day-selector_calendar-day-badge">${this.BADGE_EURO_ICON}</span>`;
                }
                dayContent += `<span class="sb_template-rtf-day-selector_calendar-day-price">${rtfInfo.price}</span>`;
            }

            let $day;
            if (!isPast && rtfInfo && isBookable) {
                // Create link for days with RTFs that are bookable
                const templateId = $calendar.data("templateId");
                const leadFormPage = $calendar.data("leadFormPage");
                const rtfId = rtfInfo.rtf_id;
                const viewMode = $calendar.data("viewMode");
                const href = `${leadFormPage}?template-id=${templateId}&rtf-id=${rtfId}&gpmpn_page=2&rtf-view=${viewMode}`;
                $day = $(
                    `<a href="${href}" class="${dayClasses}" data-date="${dateKey}">${dayContent}</a>`,
                );
            } else {
                $day = $(
                    `<div class="${dayClasses}" data-date="${dateKey}">${dayContent}</div>`,
                );
            }

            $daysContainer.append($day);
        }

        // Update navigation buttons
        this.updateNavigation($calendar);
    },

    formatDateKey(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    },

    updateNavigation($calendar) {
        const currentDate = $calendar.data("currentDate");
        const rtfData = $calendar.data("rtfData");
        const monthsWithRtfs = rtfData.months_with_rtfs || [];
        const today = new Date();

        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const currentMonthKey = `${currentYear}-${String(
            currentMonth + 1,
        ).padStart(2, "0")}`;

        // Get the first and last months with RTFs
        const firstRtfMonth =
            monthsWithRtfs.length > 0 ? monthsWithRtfs[0] : null;
        const lastRtfMonth =
            monthsWithRtfs.length > 0
                ? monthsWithRtfs[monthsWithRtfs.length - 1]
                : null;

        // Calculate the earliest allowed month (current month or first RTF month, whichever is later)
        const todayMonthKey = `${today.getFullYear()}-${String(
            today.getMonth() + 1,
        ).padStart(2, "0")}`;
        const earliestAllowedMonth =
            firstRtfMonth && firstRtfMonth > todayMonthKey
                ? firstRtfMonth
                : todayMonthKey;

        // Disable prev button if at earliest allowed month
        const disablePrev = currentMonthKey <= earliestAllowedMonth;
        $calendar.find("[data-calendar-prev]").prop("disabled", disablePrev);

        // Disable next button if at the last month with RTFs
        const disableNext = !lastRtfMonth || currentMonthKey >= lastRtfMonth;
        $calendar.find("[data-calendar-next]").prop("disabled", disableNext);
    },

    bindEvents($calendar) {
        // Unbind previous handlers to prevent duplicates on re-init
        $calendar.find("[data-calendar-prev]").off("click.rtfCalendar");
        $calendar.find("[data-calendar-next]").off("click.rtfCalendar");
        $calendar.find("[data-view-toggle]").off("click.rtfCalendar");

        $calendar.find("[data-calendar-prev]").on("click.rtfCalendar", () => {
            this.navigateMonth($calendar, -1);
        });

        $calendar.find("[data-calendar-next]").on("click.rtfCalendar", () => {
            this.navigateMonth($calendar, 1);
        });

        // View toggle
        $calendar.find("[data-view-toggle]").on("click.rtfCalendar", (e) => {
            const viewMode = $(e.currentTarget).attr("data-view-toggle");
            this.setViewMode($calendar, viewMode);
        });

        // A click anywhere on an available list row is a click on its "Boek nu" link, so the
        // handler below (availability check, saving form data) runs exactly as it does there
        $calendar.off("click.rtfCalendarRow");
        $calendar.on(
            "click.rtfCalendarRow",
            ".sb_template-rtf-day-selector_list-row:not(.s_unavailable)",
            (e) => {
                if ($(e.target).closest("a").length) {
                    return;
                }

                $(e.currentTarget).find("a[href*='rtf-id']").get(0)?.click();
            },
        );

        // Save form data before navigating to a different date
        // Use event delegation since links are dynamically rendered
        // Also check availability before allowing navigation
        $calendar.off("click.rtfCalendarNav", "a[href*='rtf-id']");
        $calendar.on("click.rtfCalendarNav", "a[href*='rtf-id']", (e) => {
            const $link = $(e.currentTarget);
            const href = $link.attr("href");

            // Extract rtf-id from the link
            const urlParams = new URLSearchParams(href.split("?")[1] || "");
            const targetRtfId = urlParams.get("rtf-id");

            if (targetRtfId) {
                // Check if we need to show availability warning
                const availabilityCheck = this.checkAvailabilityForAdults(
                    $calendar,
                    targetRtfId,
                );

                if (availabilityCheck.showWarning) {
                    e.preventDefault();

                    const spotsWord =
                        availabilityCheck.availableSpots === 1
                            ? "plek"
                            : "plekken";
                    const confirmed = window.confirm(
                        `Let op: je hebt ${availabilityCheck.adultsAmount} volwassenen geselecteerd, maar voor deze datum zijn er nog maar ${availabilityCheck.availableSpots} ${spotsWord} beschikbaar. Als je doorgaat zetten we het aantal terug naar deze beschikbaarheid.\n\nWil je toch doorgaan?`,
                    );

                    if (confirmed) {
                        this.saveFormData();
                        window.location.href = href;
                    }
                    return;
                }
            }

            this.saveFormData();
        });
    },

    /**
     * Check if the user's selected adults_amount exceeds the available spots for a target RTF
     * @param {jQuery} $calendar - The calendar element
     * @param {string} targetRtfId - The RTF ID to check availability for
     * @returns {Object} - { showWarning: boolean, adultsAmount: number, availableSpots: number }
     */
    checkAvailabilityForAdults($calendar, targetRtfId) {
        // Get the current adults_amount from localStorage or form
        let adultsAmount = 0;

        // Try localStorage first
        try {
            const savedData = localStorage.getItem(this.STORAGE_KEY);
            if (savedData) {
                const formData = JSON.parse(savedData);
                // Find the adults_amount field (could be input_X format)
                Object.entries(formData).forEach(([key, value]) => {
                    if (key.includes("input_") && parseInt(value, 10) > 0) {
                        // Check if this might be the adults field by looking at the form
                        const $input = $(`[name="${key}"]`);
                        if (
                            $input.closest(".gfield").find('[class*="adults"]')
                                .length > 0 ||
                            $input
                                .closest(".gfield")
                                .attr("data-sb-field-name") === "adults_amount"
                        ) {
                            adultsAmount = parseInt(value, 10);
                        }
                    }
                });
            }
        } catch (e) {
            // Ignore parse errors
        }

        // Also check the form directly (in case it's not saved yet)
        const $adultsInput = $(
            '[data-sb-field-name="adults_amount"] input[type="number"], [data-sb-field-name="adults_amount"] input[type="text"]',
        );
        if ($adultsInput.length && $adultsInput.val()) {
            const formAdults = parseInt($adultsInput.val(), 10);
            if (formAdults > adultsAmount) {
                adultsAmount = formAdults;
            }
        }

        // If no adults selected, no warning needed
        if (adultsAmount <= 0) {
            return { showWarning: false, adultsAmount: 0, availableSpots: 0 };
        }

        // Find the RTF info for the target RTF
        const rtfData = $calendar.data("rtfData");
        const lowestPrices = rtfData.lowest_prices_by_date || {};

        let targetRtfInfo = null;
        Object.values(lowestPrices).forEach((rtfInfo) => {
            if (String(rtfInfo.rtf_id) === String(targetRtfId)) {
                targetRtfInfo = rtfInfo;
            }
        });

        if (!targetRtfInfo) {
            return { showWarning: false, adultsAmount, availableSpots: 0 };
        }

        // Check if availability is unlimited
        if (targetRtfInfo.availability === -1) {
            return {
                showWarning: false,
                adultsAmount,
                availableSpots: Infinity,
            };
        }

        // Calculate available spots
        const totalSpots = targetRtfInfo.availability || 0;
        const bookedSpots = targetRtfInfo.total_persons || 0;
        const availableSpots = totalSpots - bookedSpots;

        // Show warning if adults_amount exceeds available spots
        if (adultsAmount > availableSpots) {
            return { showWarning: true, adultsAmount, availableSpots };
        }

        return { showWarning: false, adultsAmount, availableSpots };
    },

    setViewMode($calendar, viewMode) {
        $calendar.data("viewMode", viewMode);

        // Update URL query parameter
        const url = new URL(window.location.href);
        url.searchParams.set("rtf-view", viewMode);
        window.history.replaceState({}, "", url);

        // Also update the GF form action URL so view mode persists through page navigation
        const $gfForm = $('form[id^="gform_"]').first();
        if ($gfForm.length) {
            try {
                const currentAction =
                    $gfForm.attr("action") || window.location.href;
                const formAction = new URL(
                    currentAction,
                    window.location.origin,
                );
                formAction.searchParams.set("rtf-view", viewMode);
                $gfForm.attr("action", formAction.toString());
            } catch (e) {
                // Ignore URL parse errors
            }
        }

        // Update toggle buttons - set all to ghost, then active to default
        $calendar
            .find("[data-view-toggle]")
            .removeClass("s_active")
            .attr("data-variant", "ghost");
        $calendar
            .find(`[data-view-toggle="${viewMode}"]`)
            .addClass("s_active")
            .attr("data-variant", "default");

        // Show/hide views
        if (viewMode === "calendar") {
            $calendar.find('[data-view="calendar"]').removeClass("s_hidden");
            $calendar.find('[data-view="list"]').addClass("s_hidden");
        } else {
            $calendar.find('[data-view="calendar"]').addClass("s_hidden");
            $calendar.find('[data-view="list"]').removeClass("s_hidden");
            // Re-render list when switching to it
            this.renderList($calendar);
        }
    },

    renderList($calendar) {
        const rtfData = $calendar.data("rtfData");
        const locale = $calendar.data("locale");
        const templateId = $calendar.data("templateId");
        const leadFormPage = $calendar.data("leadFormPage");
        const currentDate = $calendar.data("currentDate");
        const lowestPrices = rtfData.lowest_prices_by_date || {};
        const lowestPriceByMonth = rtfData.lowest_price_by_month || {};

        const $listContainer = $calendar.find("[data-rtf-list]");
        $listContainer.empty();

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Get current month key (YYYY-MM)
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const monthKey = `${currentYear}-${String(currentMonth + 1).padStart(
            2,
            "0",
        )}`;

        // Get lowest price for this month
        const lowestPriceOfMonth = lowestPriceByMonth[monthKey]?.price;

        // Get dates for current month only, sorted chronologically
        const sortedDates = Object.keys(lowestPrices)
            .filter((dateKey) => {
                const date = new Date(dateKey);
                return date >= today && dateKey.startsWith(monthKey);
            })
            .sort();

        if (sortedDates.length === 0) {
            $listContainer.append(
                '<div class="sb_template-rtf-day-selector_list-empty">Geen beschikbare data gevonden.</div>',
            );
            return;
        }

        // Get selected RTF ID from URL
        const urlParams = new URLSearchParams(window.location.search);
        const selectedRtfId = urlParams.get("rtf-id");

        sortedDates.forEach((dateKey) => {
            const rtfInfo = lowestPrices[dateKey];
            const date = new Date(dateKey);

            // Check if this is the lowest price of the month
            const isLowestPrice =
                lowestPriceOfMonth !== undefined &&
                parseFloat(rtfInfo.price) === parseFloat(lowestPriceOfMonth);

            // Check availability
            // availability = -1 means unlimited availability
            // availability = total spots, total_persons = booked spots
            const hasUnlimitedAvailability = rtfInfo.availability === -1;
            let isAvailable = false;
            let availableSpots = 0;
            let totalSpots = 0;
            let availabilityPercent = 0;
            let availabilityStatus = "red";

            if (hasUnlimitedAvailability) {
                isAvailable = rtfInfo.available !== false;
            } else {
                totalSpots = rtfInfo.availability || 0;
                const bookedSpots = rtfInfo.total_persons || 0;
                availableSpots = totalSpots - bookedSpots;
                isAvailable = rtfInfo.available !== false && availableSpots > 0;
                availabilityPercent =
                    totalSpots > 0 ? (availableSpots / totalSpots) * 100 : 0;

                // Determine availability status: green (>50%), orange (1-50%), red (0 or closed)
                if (isAvailable && availableSpots > 0) {
                    availabilityStatus =
                        availabilityPercent > 50 ? "green" : "orange";
                }
            }

            // Format date: "15 januari 2025"
            const formattedDate = date.toLocaleDateString(locale, {
                day: "numeric",
                month: "short",
                year: "numeric",
            });

            // Format price
            const formattedPrice = Math.round(rtfInfo.price).toLocaleString("nl-NL");

            // Build link URL
            const viewMode = $calendar.data("viewMode");
            const href = `${leadFormPage}?template-id=${templateId}&rtf-id=${rtfInfo.rtf_id}&gpmpn_page=2&rtf-view=${viewMode}`;

            // Check if this row is selected
            const isSelected =
                selectedRtfId && String(rtfInfo.rtf_id) === selectedRtfId;

            // Build row classes
            let rowClasses = "sb_template-rtf-day-selector_list-row";
            if (isLowestPrice && isAvailable) {
                rowClasses += " s_lowest";
            }
            if (!isAvailable) {
                rowClasses += " s_unavailable";
            }
            if (isSelected) {
                rowClasses += " s_selected";
            }

            // Build book button or unavailable text
            const bookButton = isAvailable
                ? `<a href="${href}" class="sb_btn sb_template-rtf-day-selector_list-btn" data-variant="primary" data-color="green" data-icon-position="right" data-size="m">
                        Boek nu
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                    </a>`
                : `<div class="sb_btn sb_template-rtf-day-selector_list-btn" data-variant="secondary" data-color="gray-500" data-size="m">
                        Volgeboekt
                    </div>`;

            // Build availability column (only for limited availability)
            let availabilityColumn = "";
            if (!hasUnlimitedAvailability) {
                const availabilityText = isAvailable
                    ? `${availableSpots} plekken beschikbaar`
                    : "0 plekken beschikbaar";
                availabilityColumn = `
                    <span class="sb_template-rtf-day-selector_list-availability">
                        <span class="sb_template-rtf-day-selector_list-availability-dot s_${availabilityStatus}"></span>
                        <span class="sb_template-rtf-day-selector_list-availability-text">${availabilityText}</span>
                    </span>`;
            } else {
                availabilityColumn = `<span class="sb_template-rtf-day-selector_list-availability"></span>`;
            }

            // Description columns
            const ageGroup = rtfInfo.age_group || "";
            const shortDescription = rtfInfo.short_description || "";

            // Build price column with optional badge
            const lowestBadge =
                isLowestPrice && isAvailable
                    ? `<span class="sb_template-rtf-day-selector_list-price-badge">${this.BADGE_EURO_ICON}</span>`
                    : "";

            const $row = $(`
                <div class="${rowClasses}">
                    <span class="sb_template-rtf-day-selector_list-date">${formattedDate}</span>
                    ${availabilityColumn}
                    <span class="sb_template-rtf-day-selector_list-price">${lowestBadge}${formattedPrice} <small>p.p.</small></span>
                    <span class="sb_template-rtf-day-selector_list-description">${ageGroup}</span>
                    <span class="sb_template-rtf-day-selector_list-description">${shortDescription}</span>
                    ${bookButton}
                </div>
            `);

            $listContainer.append($row);
        });
    },

    /**
     * Save current form data to sessionStorage before navigation
     * This preserves user input when changing dates
     * Only saves user-entered fields, not programmatically set values
     */
    saveFormData() {
        const formData = {};
        const $form = $('form[id^="gform_"]').first();

        if ($form.length === 0) return;

        // Get field mapping to identify programmatic fields to exclude
        const $formSection = $("[data-s-type='lead-form-booking']");
        let fieldMapping = {};
        if ($formSection.length > 0) {
            try {
                fieldMapping = JSON.parse(
                    $formSection.attr("data-field-mapping") || "{}",
                );
            } catch (e) {
                // Ignore parse errors
            }
        }

        // Fields that are programmatically set and should NOT be restored
        const programmaticFields = [
            "template_rtf_day_selector",
            "template_rtf_flight_selector",
            "template_rtf_date",
            "rtf",
            "template",
            "template_id",
            "destination",
            "price",
            "kids_price",
            "total",
            "overview_sections",
        ];

        // Also exclude all snapshot fields
        Object.keys(fieldMapping).forEach((key) => {
            if (key.startsWith("snapshot_")) {
                programmaticFields.push(key);
            }
        });

        // Build list of field IDs to exclude
        const excludedFieldIds = programmaticFields
            .map((name) => fieldMapping[name])
            .filter(Boolean);

        $form.find(":input").each(function () {
            const $input = $(this);
            const name = $input.attr("name");

            // Skip GF internal fields, nonces, and empty names
            if (
                !name ||
                name.startsWith("gform_") ||
                name.includes("_nonce") ||
                name.includes("_wpnonce") ||
                name === "is_submit" ||
                name === "state_" ||
                name.startsWith("input_") === false
            ) {
                return;
            }

            // Extract field ID from input name (e.g., "input_30" -> "30", "input_30.2" -> "30")
            const fieldIdMatch = name.match(/^input_(\d+)/);
            if (fieldIdMatch) {
                const fieldId = fieldIdMatch[1];
                if (excludedFieldIds.includes(fieldId)) {
                    return; // Skip programmatic fields
                }
            }

            // Handle checkboxes and radio buttons
            if ($input.is(":checkbox")) {
                if ($input.is(":checked")) {
                    if (!formData[name]) {
                        formData[name] = [];
                    }
                    formData[name].push($input.val());
                }
            } else if ($input.is(":radio")) {
                if ($input.is(":checked")) {
                    formData[name] = $input.val();
                }
            } else {
                formData[name] = $input.val();
            }
        });

        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(formData));
            console.debug("[RTF Calendar] Saved form data:", formData);
        } catch (e) {
            console.warn("[RTF Calendar] Failed to save form data:", e);
        }
    },

    navigateMonth($calendar, direction) {
        const currentDate = $calendar.data("currentDate");
        const rtfData = $calendar.data("rtfData");
        const monthsWithRtfs = rtfData.months_with_rtfs || [];
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + direction);

        const newMonthKey = `${newDate.getFullYear()}-${String(
            newDate.getMonth() + 1,
        ).padStart(2, "0")}`;
        const today = new Date();
        const todayMonthKey = `${today.getFullYear()}-${String(
            today.getMonth() + 1,
        ).padStart(2, "0")}`;

        // Get the first and last months with RTFs
        const firstRtfMonth =
            monthsWithRtfs.length > 0 ? monthsWithRtfs[0] : null;
        const lastRtfMonth =
            monthsWithRtfs.length > 0
                ? monthsWithRtfs[monthsWithRtfs.length - 1]
                : null;

        // Calculate the earliest allowed month
        const earliestAllowedMonth =
            firstRtfMonth && firstRtfMonth > todayMonthKey
                ? firstRtfMonth
                : todayMonthKey;

        // Don't allow navigating before earliest allowed month
        if (newMonthKey < earliestAllowedMonth) {
            return;
        }

        // Don't allow navigating past the last month with RTFs
        if (lastRtfMonth && newMonthKey > lastRtfMonth) {
            return;
        }

        $calendar.data("currentDate", newDate);
        this.renderMonth($calendar);
        this.renderList($calendar);
        this.updateActiveTab($calendar);
    },
};

// Initialize on document ready
$(() => {
    RTFCalendar.init();
});

// Expose globally for dynamic loading
window.RTFCalendar = RTFCalendar;

// Listen for dynamically loaded calendars
window.addEventListener("rtf-calendar-loaded", (event) => {
    if (event.detail && event.detail.element) {
        RTFCalendar.initCalendar($(event.detail.element));
    }
});

export default RTFCalendar;
